import { Issue, IssueTypes } from '@hud/portal-dtos/issues.dtos';
import {
  RelativeTimeFrame,
  ThresholdFilter,
  TimeFrame,
} from '@hud/portal-dtos/webapp-common.dto';
import {
  formatPrice,
  formatPercentage,
  formatDuration,
  formatNumber,
  formatBigNumbers,
} from '@hud/web-components/utils';
import { addDays, min, subDays } from 'date-fns';

export interface MetricProps {
  value: number;
  format?:
    | 'number'
    | 'currency'
    | 'percent'
    | 'duration'
    | 'durationAllowNegative'
    | 'numberNullZero'
    | 'abbreviated';
}

export function formatMetricValue({ value, format }: MetricProps) {
  let formatted = '';
  if (format === 'numberNullZero') {
    if (value === 0 || value === null || value === undefined) {
      return '-';
    }
    formatted = formatNumber(value);
  } else if (format === 'currency') {
    formatted = formatPrice(value);
  } else if (format === 'percent') {
    formatted = formatPercentage(value);
  } else if (format === 'duration') {
    formatted = formatDuration(value);
  } else if (format === 'durationAllowNegative') {
    formatted = value >= 0 ? formatDuration(value) : `-${formatDuration(-value)}`;
  } else if (format === 'abbreviated') {
    formatted = formatBigNumbers(value);
  } else {
    formatted = formatNumber(value);
  }
  return formatted;
}

export function getFileName(filePath: string) {
  // Split the filePath by '/' to get an array of parts
  const parts = filePath.split('/');

  // Get the last part which is the filename
  const fileName = parts.pop();

  // If the original filePath contains directories, add './' prefix
  if (parts.length > 0) {
    return `./${fileName}`;
  }

  // If the filePath is just the filename, return it as is
  return fileName;
}
export function isUnnamed(funcName: string) {
  const unnamedFunctions = ['=>', '<anon>'];
  // return true if funName contains any of the unnamed functions as a substring
  return unnamedFunctions.some((unnamed) => funcName.includes(unnamed));
}

export function getIssueTimeFrame(issue: Issue) {
  const TIMEFRAME_DAYS_WOW = 14;
  const TIMEFRAME_DAYS_2DO2D = 4;
  const TIMEFRAME_DAYS_SPIKE = 1;
  const TIMEFRAME_DAYS_POST_DEPLOY = 3;
  const TIMEFRAME_DAYS_NEW_ERROR_START = 7;
  const TIMEFRAME_DAYS_NEW_ERROR_END = 1;
  const TIMEFRAME_DAYS_DEFAULT = 7;
  if (
    issue.type === IssueTypes.endpoint_degradation_post_deployment ||
    issue.type === IssueTypes.kafka_topic_consume_degradation_post_deployment
  ) {
    const deploymentTime = issue.mainEntity.details.deploymentTime;
    if (!deploymentTime) throw new Error('Deployment time is missing');

    return {
      // use getIssueTimeFrame after includes this logic
      start: subDays(deploymentTime, TIMEFRAME_DAYS_POST_DEPLOY),
      end: issue.detectionTime,
    };
  }

  if (issue.type === IssueTypes.new_error) {
    return {
      start: subDays(issue.detectionTime, TIMEFRAME_DAYS_NEW_ERROR_START),
      end:
        min([addDays(issue.detectionTime, TIMEFRAME_DAYS_NEW_ERROR_END), new Date()]) ||
        new Date(), // The or shouldn't be reached
    };
  }

  const timeFrameStartDaysAgoMap: { [key in IssueTypes]?: number } = {
    [IssueTypes.endpoint_degradation_wow]: TIMEFRAME_DAYS_WOW,
    [IssueTypes.endpoint_degradation_2do2d]: TIMEFRAME_DAYS_2DO2D,
    [IssueTypes.endpoint_degradation_spike]: TIMEFRAME_DAYS_SPIKE,
    [IssueTypes.kafka_topic_consume_degradation_wow]: TIMEFRAME_DAYS_WOW,
    [IssueTypes.kafka_topic_consume_degradation_2do2d]: TIMEFRAME_DAYS_2DO2D,
    [IssueTypes.generic]: TIMEFRAME_DAYS_DEFAULT,
  };

  const days = timeFrameStartDaysAgoMap[issue.type];
  if (days === undefined) {
    throw new Error(`Unsupported issue type: ${issue.type}`);
  }

  return {
    start: subDays(issue.detectionTime, days),
    end: issue.detectionTime,
  };
}

export function formatFlowType(flowType?: string) {
  if (!flowType) {
    return '';
  }
  return flowType === 'SQS'
    ? flowType
    : flowType.charAt(0) + flowType.slice(1).toLowerCase();
}

const RANGE_DELIMITER = '-';
const pad = (num: number) => num.toString().padStart(2, '0');

export const stringifyTimeFrame = (
  timeFrame: TimeFrame,
  options?: { normalize?: boolean },
): string => {
  if (typeof timeFrame === 'string') {
    return timeFrame;
  } else {
    let startDate = timeFrame.start;
    let endDate = timeFrame.end;

    if (options?.normalize) {
      // Round start date to beginning of hour
      const roundedStart = new Date(timeFrame.start);
      roundedStart.setMinutes(0, 0, 0);
      startDate = roundedStart;

      // Round end date to end of hour
      const roundedEnd = new Date(timeFrame.end);
      roundedEnd.setMinutes(59, 59, 999);
      endDate = roundedEnd;
    }

    return `${encodeDate(startDate)}${RANGE_DELIMITER}${encodeDate(endDate)}`;
  }
};

export const parseTimeFrame = (stored: string | null): TimeFrame => {
  if (!stored) return 'week'; // Default value
  // if stored in RelativeTimeFrame format, return as is
  if (RelativeTimeFrame.options.includes(stored as RelativeTimeFrame)) {
    return stored as RelativeTimeFrame;
  }
  try {
    if (stored.includes(RANGE_DELIMITER)) {
      const [start, end] = stored.split(RANGE_DELIMITER);
      return { start: decodeDate(start), end: decodeDate(end) };
    }
  } catch (error) {
    console.error('Failed to parse stored time frame:', error);
  }
  return 'week'; // Fallback to default value
};

export const encodeDate = (date: Date) => {
  const year = date.getUTCFullYear();
  const month = pad(date.getUTCMonth() + 1); // Months are 0-based
  const day = pad(date.getUTCDate());
  const hours = pad(date.getUTCHours());
  const minutes = pad(date.getUTCMinutes());

  return `${year}${month}${day}_${hours}${minutes}`;
};

export const decodeDate = (dateString: string) => {
  const match = /^(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})$/.exec(dateString);
  if (!match) throw new Error('Invalid date format');

  const [, year, month, day, hours, minutes] = match.map(Number);
  return new Date(Date.UTC(year, month - 1, day, hours, minutes));
};

export type ThresholdFilterKey = 'durationAvg' | 'normalizedRuns' | 'errorRate';

export type CommonThresholdQueryParams = {
  durationAvg?: ThresholdFilter['value'];
  durationAvg_op?: ThresholdFilter['operator'];
  normalizedRuns?: ThresholdFilter['value'];
  normalizedRuns_op?: ThresholdFilter['operator'];
  errorRate?: ThresholdFilter['value'];
  errorRate_op?: ThresholdFilter['operator'];
};

export function getThresholdFilter(
  value: number | undefined,
  operator: 'gte' | 'lte' | undefined,
): ThresholdFilter | undefined {
  return value
    ? {
        value,
        operator: operator ?? 'gte',
      }
    : undefined;
}

// Returns the method name, or (+X) if there is more than 1 method
export function getMethodsDisplay(methods: string[]): {
  methodsToPresent: string;
  methodOverflowString: string;
} {
  if (methods.length === 0) {
    return {
      methodsToPresent: '',
      methodOverflowString: '',
    };
  }

  if (methods.length === 1) {
    return {
      methodsToPresent: methods[0].toUpperCase(),
      methodOverflowString: '',
    };
  }

  const methodsToPresent: string = methods
    .filter((m) => ['get', 'post'].includes(m))
    .slice(0, 1)
    .map((m) => m.toUpperCase())
    .join(', ');

  const methodOverflowString = methods.length > 1 ? `+(${methods.length - 1})` : '';

  return {
    methodsToPresent,
    methodOverflowString,
  };
}

export function getMethodsDisplayText(methods: string[]): string {
  const { methodsToPresent, methodOverflowString } = getMethodsDisplay(methods);
  return `${methodsToPresent}${methodOverflowString}`;
}
